import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import Modal from '../Modal';
import ResultGrid from './ResultGrid';
import { getActionSelectStyles } from '../../utility/selectStyles';
import { Delimiter, DELIMITER_LABEL, detectDelimiter, humanSize, readCsv, resolveHeader } from './csv';
import { LoadedCsv } from './csvFile';
import { UploadOutcome, importFailureLine, uploadProgress } from './importUpload';

/** How the commit ended, built by the view (which knows the applied count and the refusal's details). */
export interface ImportEnd { ok: boolean; line: string; postgres?: string }

interface ImportModalProps {
    table: string;
    /** The table's own column names, for matching the file's header (exactly first, then ignoring case). */
    tableColumns: string[];
    file: LoadedCsv;
    expert: boolean;
    /** Upload the whole file in chunks; resolves with the import id and whole-file hash, or why it stopped. */
    upload: (bytes: Uint8Array, onProgress: (sent: number, total: number) => void, cancelled: () => boolean) => Promise<UploadOutcome>;
    /** Commit the staged file into this table; the view words the outcome. */
    commit: (importId: string, sha256: string, delimiter: Delimiter, columns: string[]) => Promise<ImportEnd>;
    /** The commit applied: the view reads the page again. */
    onSucceeded: () => void;
    onClose: () => void;
}

const MAX_BYTES = 20 * 1024 * 1024;
const DELIMITERS: Delimiter[] = ['comma', 'semicolon', 'tab', 'pipe'];

/**
 * DESIGN_EDITING.md console flow 9: import a CSV into an existing table. The file is already read (bytes + a decoded
 * preview). The modal shows the size, the delimiter, a 5-row preview, and matches the header to the table's columns;
 * Import is off until the header fits. The upload and commit are injected, so this component stays presentational.
 */
const ImportModal: React.FC<ImportModalProps> = ({ table, tableColumns, file, expert, upload, commit, onSucceeded, onClose }) => {
    const [delimiter, setDelimiter] = useState<Delimiter>(() => (file.text ? detectDelimiter(file.text) : 'comma'));
    // 'ready' before Import; 'running' during upload/commit; 'done' after a success; 'failed' with a line to read.
    const [phase, setPhase] = useState<'ready' | 'running' | 'done' | 'failed'>('ready');
    const [busy, setBusy] = useState<string | null>(null);
    const [line, setLine] = useState<string | null>(null);
    const [postgres, setPostgres] = useState<string | null>(null);
    const running = phase === 'running';

    const shape = useMemo(() => (file.text ? readCsv(file.text, delimiter, 5) : { header: [], rows: [] }), [file.text, delimiter]);
    const match = useMemo(() => resolveHeader(shape.header, tableColumns), [shape.header, tableColumns]);

    // One blocking line, in the order the design lists: not UTF-8, too large, then a header that does not fit.
    const blocker =
        file.text === null ? 'Not UTF-8.'
        : file.size > MAX_BYTES ? 'File too large.'
        : shape.header.length === 0 ? 'The file has no header row.'
        : match.notInTable.length > 0 ? `Not in this table: ${match.notInTable.join(', ')}.`
        : match.columns.length === 0 ? 'No columns to import.'
        : null;

    const run = async () => {
        if (blocker || running) return;
        setPhase('running');
        setLine(null);
        setPostgres(null);
        setBusy(uploadProgress(0, file.size));
        const up = await upload(file.bytes, (sent, total) => setBusy(uploadProgress(sent, total)), () => false);
        if (!up.ok) {
            // A cancel leaves nothing to say; anything else is worded.
            if (up.reason === 'cancelled') { setPhase('ready'); setBusy(null); return; }
            setPhase('failed'); setBusy(null); setLine(importFailureLine(up));
            return;
        }
        setBusy('Importing...');
        const end = await commit(up.importId, up.sha256, delimiter, match.columns);
        setBusy(null);
        setLine(end.line);
        setPostgres(end.postgres ?? null);
        if (end.ok) { setPhase('done'); onSucceeded(); } else setPhase('failed');
    };

    const canClose = !running;
    return (
        <Modal isOpen onClose={() => { if (canClose) onClose(); }} title={`Import into ${table}`} size="md" closeOnEscape={canClose} showCloseButton={canClose}>
            <div className="db-data-import">
                <p className="db-data-import-file">{file.fileName} · {humanSize(file.size)}</p>

                <div className="db-data-import-delimiter">
                    <label id="db-import-delim-label">Delimiter</label>
                    <Select
                        aria-labelledby="db-import-delim-label"
                        options={DELIMITERS.map((d) => ({ value: d, label: DELIMITER_LABEL[d] }))}
                        value={{ value: delimiter, label: DELIMITER_LABEL[delimiter] }}
                        isDisabled={running || file.text === null}
                        isSearchable={false}
                        onChange={(o: any) => setDelimiter((o?.value ?? 'comma') as Delimiter)}
                        styles={getActionSelectStyles()}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>

                {file.text !== null && shape.header.length > 0 && (
                    <div className="db-data-import-preview">
                        <ResultGrid label={`First rows of ${file.fileName}`} columns={shape.header} rows={shape.rows} emptyText="No rows." truncatedText={null} />
                    </div>
                )}

                {blocker && <p className="db-data-field-line">{blocker}</p>}
                {line && <p className="db-data-write-line" role={phase === 'failed' ? 'alert' : 'status'}><span>{line}</span></p>}
                {expert && postgres && <pre className="db-data-write-postgres">{postgres}</pre>}

                <div className="modal-actions">
                    {phase === 'done' ? (
                        <button type="button" className="submit-button" onClick={onClose}>Close</button>
                    ) : (
                        <>
                            <button type="button" className="cancel-button" onClick={onClose} disabled={running}>Cancel</button>
                            <button type="button" className="submit-button" onClick={run} disabled={running || !!blocker}>{busy ?? 'Import'}</button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ImportModal;
